import React, { memo, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import Divider from 'LuxKit/Divider'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceDetails from 'LuxKit/PricePoints/PriceRowPriceDetails'

interface Props {
  experience?: App.ExperienceOffer
  ticket?: App.ExperienceBookingTicketView
  ticketCount?: number
  /**
   * @default horizontal
   */
  orientation?: 'horizontal' | 'vertical'
}

const Container = styled.div`
  width: max-content;
`

const StyledGroup = styled(Group)`
  padding-right: ${rem(8)};
`

function ExperiencePriceDetails(props: Props) {
  const intl = useIntl()
  const { pathname } = useLocation()
  const { on: open, off: close, value: isOpen } = useToggle()
  const [expanded, setExpanded] = useState<boolean>(false)

  const { experience, ticket, ticketCount, orientation = 'horizontal' } = props

  const expand = () => setExpanded(!expanded)

  const prices = useMemo(() => {
    let price = 0
    let taxesAndFees = 0
    let priceBeforeTax = 0

    if (ticket?.taxesAndFees) {
      taxesAndFees = ticketCount ? (ticket.taxesAndFees * ticketCount) : ticket.taxesAndFees
      price = ticketCount ? (ticket.price * ticketCount) : ticket.price
      priceBeforeTax = price - taxesAndFees
    }

    if (experience?.taxesAndFees) {
      taxesAndFees = experience.taxesAndFees
      price = experience.price // price is already coming with the taxesAndFees included when the offers has taxesAndFees
      priceBeforeTax = experience.price - experience.taxesAndFees
    }

    return { price, priceBeforeTax, taxesAndFees }
  }, [experience, ticket, ticketCount])

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation()
    e.preventDefault()
    open()
  }, [open])

  const readLessLabel = intl.formatMessage({
    id: 'n5/00TuWne',
    defaultMessage: 'Read Less',
    description: 'Toggle button text to collapse tax explanation in experience price details',
  })

  const learnMoreLabel = intl.formatMessage({
    id: 'apZDoDRozx',
    defaultMessage: 'Learn More',
    description: 'Toggle button text to expand tax explanation in experience price details',
  })

  const showFooter = useMemo(() => experience && !pathname.includes(experience?.id), [experience, pathname])

  const modalFooterSecondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: (
      <TextButton kind="primary" fit="flex" to={`/experience/${experience?.slug}/${experience?.id}`}>
        {intl.formatMessage({
          id: 'NeJpdlbOCL',
          defaultMessage: 'View Offer',
          description: 'Button text to view the experience offer from price details modal',
        })}
      </TextButton>
    ),
    onClick: close,
    as: 'div',
    kind: 'secondary',
  }), [close, experience, intl])

  return <Container>
    <Group direction={orientation}>
      <PriceRowTaxesAndFees
        endIcon={(!!prices.taxesAndFees && orientation === 'horizontal') && <Clickable onClick={handleClick}>
          <LineInfoCircleIcon />
        </Clickable>}
      />
      {(!!prices.taxesAndFees && orientation === 'vertical') && <PriceRowPriceDetails
        size="M"
        onClick={handleClick}
      />}
    </Group>
    <ModalBase
      size="L"
      isOpen={isOpen}
      onClose={close}
    >
      <ModalHeader
        title={intl.formatMessage({
          id: 'RDdv//AzAa',
          defaultMessage: 'Price details',
          description: 'Modal title for experience price details for the experience',
        })}
        onCloseButtonClick={close}
      />
      <ModalBody>
        <ModalContent>
          <StyledGroup direction="vertical" gap={12}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">
                {experience?.name ?? ticket?.name}
              </BodyText>
              <BodyText as="span" variant="medium">
                <FormatCurrency value={prices.priceBeforeTax} />
              </BodyText>
            </Group>

            <Divider kind="secondary" />

            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: '002qAupMZl',
                  defaultMessage: 'Taxes and fees',
                  description: 'Label for taxes and fees row in experience price details modal',
                })}
              </BodyText>
              <BodyText variant="medium">
                <FormatCurrency value={prices.taxesAndFees} />
              </BodyText>
            </Group>
            {expanded && <BodyText as="p" variant="medium">
              <FormattedMessage
                id="11PS86iQB8"
                defaultMessage="This charge includes estimated amounts the service provider pays for their taxes and fees and/or taxes and fees that we pay to authorities on your booking (including but not limited to sales, environmental, entertainment, fuel, city, state and value-added tax) – see our <link>Terms and Conditions</link> here. Please refer to the Fine Print for any additional costs payable directly to the provider."
                description="Tax explanation text in experience price details modal"
                values={{
                  link: (chunks) => <TextLink target="_blank" to="/terms-and-conditions" underline>{chunks}</TextLink>,
                }}
              />
            </BodyText>}
            <SmallToggleButton onClick={expand} on={expanded} size="small">
              {expanded ? readLessLabel : learnMoreLabel}
            </SmallToggleButton>

            <Divider kind="secondary" />

            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: 'A/7GOKg+Tc',
                  defaultMessage: 'Total',
                  description: 'Label for total price row in experience price details modal',
                })}
              </BodyText>
              <BodyText variant="large" weight="bold">
                <FormatCurrency value={prices.price} />
              </BodyText>
            </Group>
          </StyledGroup>
        </ModalContent>
      </ModalBody>
      {showFooter && <ModalFooter secondaryActionProps={modalFooterSecondaryActionProps} />}
    </ModalBase>

  </Container>
}

ExperiencePriceDetails.displayName = 'ExperiencePriceDetails'

export default memo(ExperiencePriceDetails)
